import * as fs from 'fs';
import * as path from 'path';

import { makeNamespacedLog } from '@sb/log';

import type { LocalCaptureSettings } from './localCaptureSettings';

const log = makeNamespacedLog('pruneCaptureDirectory');

/**
 * How long an in-progress write is left alone before it counts as abandoned.
 *
 * A local write is copy-to-`.part` then rename, and the cleanup that removes a
 * partial lives in the writer's own catch block — so it runs for a failed copy
 * or rename but NOT for the process dying. A SIGKILL or power loss between the
 * two leaves a `.part` with nothing alive to remove it.
 *
 * Skipping every `.part` uniformly would orphan those permanently: under the
 * generated-filename default each capture picks a new name, so nothing ever
 * revisits the stale one to overwrite it. An hour is long enough that no
 * genuine in-flight write is ever collected — a capture is one encode and one
 * copy — and short enough that a crashed one does not outlive its usefulness.
 */
const PART_GRACE_MS = 60 * 60 * 1000;

const PART_SUFFIX = '.part';
const SIDECAR_SUFFIX = '_metadata.json';
const FRAME_SUFFIX = '.png';

/** One capture on disk: its frame, its record, and what they cost. */
interface CaptureEntry {
  /** Every file belonging to this capture. */
  files: string[];
  bytes: number;
  /** Newest mtime across the pair, so a late sidecar does not age the frame. */
  mtimeMs: number;
}

/**
 * Free bytes on the filesystem holding `directory`, or `null` where unknowable.
 *
 * Null rather than zero on a failure. Zero would read as "no space at all" and
 * send the floor loop through every capture on the robot, which is the one
 * outcome worse than not enforcing the floor.
 */
async function freeBytes(directory: string): Promise<number | null> {
  const stats = await fs.promises.statfs(directory).catch(() => null);

  if (!stats) return null;

  return Number(stats.bsize) * Number(stats.bavail);
}

export interface PruneOutcome {
  /** Files actually removed. */
  removed: string[];
  /** Bytes reclaimed. */
  bytes: number;
  /** Why pruning stopped, for the log line. */
  reasons: string[];
  /**
   * What the directory holds once pruning is done.
   *
   * Counted from the captures that survived, so it excludes anything pruning
   * does not account for — a stray file somebody dropped in the directory is
   * not something a storage warning should report as the skill's usage.
   */
  usedBytes: number;
  /**
   * Free bytes on the filesystem once pruning is done, where it can be read.
   *
   * `undefined` on a platform or a mount where `statfs` does not answer. The
   * floor is skipped entirely in that case rather than treated as zero free,
   * which would delete every capture on the robot.
   */
  freeBytes?: number;
  /**
   * Whether the filesystem is still under `minFreeBytes` after pruning.
   *
   * True means this directory has given up everything it can and the disk is
   * still tight, so the shortfall is somewhere else. That is the operator's
   * problem to hear about, which is why it is reported rather than logged.
   */
  isBelowFloor: boolean;
}

/**
 * The capture a filename belongs to, or `null` when it is not ours.
 *
 * Frame and sidecar share a stem, so grouping on it keeps the pair together.
 * They have to be collected together: deleting a frame and leaving its record
 * behind produces a record describing an image nobody can open.
 */
function stemFor(filename: string): string | null {
  if (filename.endsWith(SIDECAR_SUFFIX)) {
    return filename.slice(0, -SIDECAR_SUFFIX.length);
  }

  if (filename.endsWith(FRAME_SUFFIX)) {
    return filename.slice(0, -FRAME_SUFFIX.length);
  }

  return null;
}

/**
 * Groups a directory's contents into captures, reclaiming abandoned partials.
 *
 * A `.part` file is never grouped with a capture — it belongs to a write that
 * has not finished — so it is either left alone or, once past the grace period,
 * returned as its own single-file entry to be collected.
 */
async function readEntries(
  directory: string,
  now: number,
): Promise<{ entries: CaptureEntry[]; abandoned: CaptureEntry[] }> {
  const filenames = await fs.promises.readdir(directory);

  const byStem = new Map<string, CaptureEntry>();
  const abandoned: CaptureEntry[] = [];

  await Promise.all(
    filenames.map(async (filename) => {
      const full = path.join(directory, filename);

      // A file can vanish between the readdir and the stat — another prune, or
      // an operator clearing the directory. Not an error worth failing a
      // capture over.
      const stat = await fs.promises.stat(full).catch(() => null);

      if (!stat || !stat.isFile()) {
        return;
      }

      if (filename.endsWith(PART_SUFFIX)) {
        if (now - stat.mtimeMs > PART_GRACE_MS) {
          abandoned.push({
            files: [full],
            bytes: stat.size,
            mtimeMs: stat.mtimeMs,
          });
        }

        return;
      }

      const stem = stemFor(filename);

      if (stem === null) {
        return;
      }

      const existing = byStem.get(stem);

      if (existing) {
        existing.files.push(full);
        existing.bytes += stat.size;
        existing.mtimeMs = Math.max(existing.mtimeMs, stat.mtimeMs);

        return;
      }

      byStem.set(stem, {
        files: [full],
        bytes: stat.size,
        mtimeMs: stat.mtimeMs,
      });
    }),
  );

  // Oldest first, which is the order every bound below deletes in.
  const entries = [...byStem.values()].sort((a, b) => a.mtimeMs - b.mtimeMs);

  return { entries, abandoned };
}

/**
 * Brings a local capture directory back inside its configured bounds.
 *
 * Runs after a capture rather than before, so the frame just written is part of
 * what gets measured — a directory already at its limit should shed an old
 * capture rather than refuse the new one, because refusing would fail a routine
 * for a housekeeping reason.
 *
 * Deletes oldest-first by mtime, whole captures at a time, and stops the moment
 * the directory is back inside its size bound. A failure to delete any one file
 * is logged and skipped: pruning is housekeeping, and a directory slightly over
 * budget is a better outcome than a failed inspection.
 *
 * TAKES AN ALREADY-VALIDATED DIRECTORY, and deletes in it unconditionally.
 * Containment is the caller's job: `loadLocalCaptureSettings` refuses a
 * configured directory outside the captures tree, and botctl's `--directory`
 * flag runs the Go equivalent. Nothing here re-checks, because the tests drive
 * this function against an `mkdtemp` directory on purpose. A new caller that
 * skips that check gets a delete loop pointed wherever it was told, and
 * `/etc/standardbots` holds feathers-blob's objects and the robot's
 * configuration.
 */
export async function pruneCaptureDirectory(args: {
  directory: string;
  settings: LocalCaptureSettings;
  /** Injectable for tests; defaults to now. */
  now?: number;
}): Promise<PruneOutcome> {
  const { directory, settings } = args;
  const now = args.now ?? Date.now();

  const outcome: PruneOutcome = {
    removed: [],
    bytes: 0,
    reasons: [],
    usedBytes: 0,
    isBelowFloor: false,
  };

  const { entries, abandoned } = await readEntries(directory, now);

  const remove = async (entry: CaptureEntry, reason: string) => {
    for (const file of entry.files) {
      const deleted = await fs.promises
        .unlink(file)
        .then(() => true)
        .catch((error) => {
          log.warn('prune', 'Could not delete a capture file', { file, error });

          return false;
        });

      if (deleted) {
        outcome.removed.push(file);
      }
    }

    outcome.bytes += entry.bytes;

    if (!outcome.reasons.includes(reason)) {
      outcome.reasons.push(reason);
    }
  };

  // Abandoned partials first: they are pure waste, and reclaiming them may put
  // the directory back inside its bounds without touching a real capture.
  for (const partial of abandoned) {
    await remove(partial, 'abandoned-partial');
  }

  const survivors = [...entries];

  const dropOldest = async (reason: string): Promise<void> => {
    const oldest = survivors.shift();

    if (oldest) {
      await remove(oldest, reason);
    }
  };

  // Size is the whole of retention. Age and a count were bounds here once, and
  // both could delete a frame from a directory with room to spare — which is
  // the one thing rotation should never do, because the frame it sheds is a
  // defect record somebody may yet ask for.
  let total = survivors.reduce((sum, entry) => sum + entry.bytes, 0);

  while (survivors.length > 0 && total > settings.maxTotalBytes) {
    total -= survivors[0].bytes;

    await dropOldest('too-large');
  }

  outcome.usedBytes = total;

  // Free space, after the size bound and re-read as we go. It is the only bound
  // whose measurement depends on everything else on the filesystem, so a single
  // reading taken up front could over- or under-delete.
  //
  // This is what `maxTotalBytes` cannot do. That bound is per leaf, and a robot
  // has one leaf per skill, so skills that are each inside their own budget
  // still fill a disk between them. The ENOSPC that follows does not stay with
  // captures: it goes to whichever service writes next.
  let free = await freeBytes(directory);

  while (
    free !== null &&
    free < settings.minFreeBytes &&
    survivors.length > 0
  ) {
    total -= survivors[0].bytes;

    await dropOldest('low-disk');

    free = await freeBytes(directory);
  }

  outcome.usedBytes = total;
  outcome.freeBytes = free ?? undefined;

  // Still short with nothing left to give. This directory is not the problem,
  // so there is nobody for the loop above to take it out on — the operator is
  // the only one who can act.
  outcome.isBelowFloor = free !== null && free < settings.minFreeBytes;

  if (outcome.removed.length > 0) {
    log.info('prune', 'Reclaimed local capture files', {
      directory,
      files: outcome.removed.length,
      bytes: outcome.bytes,
      reasons: outcome.reasons,
    });
  }

  return outcome;
}
